/**
 * scripts-components-product.js
 * -----------------------------------------------------------------------
 * Product page behavior, split into independent pieces:
 *
 *  - `<product-gallery>`: thumbnail navigation, prev/next, syncing the
 *    active slide while the shopper swipes (native scroll-snap does the
 *    swiping — this only keeps thumbnails/dots in sync), a fullscreen
 *    dialog, and a desktop hover-zoom effect. Also listens for
 *    `am:variant:change` to jump to the selected variant's media.
 *
 *  - Variant picker: reads the JSON payload rendered by
 *    `snippets/product-variant-picker.liquid`, matches the selected
 *    option values to a variant, and updates price/SKU/availability/
 *    add-to-cart state/URL instantly — no server round trip. Also greys
 *    out option values that don't exist in any variant, and marks
 *    existing-but-unavailable combinations as sold out.
 *
 *  - Quantity selector: shared minus/input/plus behavior for both the
 *    main quantity control and the sticky bar's mirrored control.
 *
 *  - Sticky add-to-cart: reveals `snippets/sticky-cart.liquid` once the
 *    main purchase area has scrolled out of view.
 *
 *  - Share button: native Web Share API with a copy-to-clipboard
 *    fallback.
 * ----------------------------------------------------------------------- */
(function () {
  'use strict';

  var dom = window.AniMarket && window.AniMarket.dom;

  /* ------------------------------------------------------------------
   * <product-gallery>
   * ---------------------------------------------------------------- */
  class ProductGalleryElement extends HTMLElement {
    connectedCallback() {
      this.viewer = this.querySelector('[data-gallery-viewer]');
      this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
      this.thumbnails = Array.from(this.querySelectorAll('[data-gallery-thumbnail]'));
      this.prevButton = this.querySelector('[data-gallery-prev]');
      this.nextButton = this.querySelector('[data-gallery-next]');
      this.fullscreenDialog = this.querySelector('[data-gallery-fullscreen]');
      this.fullscreenTrack = this.querySelector('[data-gallery-fullscreen-track]');
      this.fullscreenClose = this.querySelector('[data-gallery-fullscreen-close]');

      this.thumbnails.forEach(
        function (thumbnail) {
          thumbnail.addEventListener('click', this._onThumbnailClick.bind(this, thumbnail));
        }.bind(this)
      );

      if (this.prevButton) this.prevButton.addEventListener('click', this._step.bind(this, -1));
      if (this.nextButton) this.nextButton.addEventListener('click', this._step.bind(this, 1));

      this.querySelectorAll('[data-gallery-zoom-trigger]').forEach(
        function (trigger) {
          trigger.addEventListener('click', this._openFullscreen.bind(this, trigger));
          trigger.addEventListener('mousemove', this._onZoomMove);
        }.bind(this)
      );

      if (this.fullscreenClose) this.fullscreenClose.addEventListener('click', this._closeFullscreen.bind(this));
      if (this.fullscreenDialog) {
        this.fullscreenDialog.addEventListener('click', this._onFullscreenBackdropClick.bind(this));
      }

      if (this.viewer && 'IntersectionObserver' in window) {
        this._observer = new IntersectionObserver(this._onSlideIntersect.bind(this), {
          root: this.viewer,
          threshold: 0.6,
        });
        this.slides.forEach(
          function (slide) {
            this._observer.observe(slide);
          }.bind(this)
        );
      }

      // `am:variant:change` is dispatched on `[data-product-root]` (an
      // ancestor of this element), so it must be listened for there, not
      // on `this` — a bubbling event never reaches a descendant.
      var productRoot = this.closest('[data-product-root]');
      if (productRoot) productRoot.addEventListener('am:variant:change', this._onVariantChange.bind(this));
    }

    _onZoomMove(event) {
      var rect = event.currentTarget.getBoundingClientRect();
      var x = ((event.clientX - rect.left) / rect.width) * 100;
      var y = ((event.clientY - rect.top) / rect.height) * 100;
      event.currentTarget.style.setProperty('--zoom-x', x + '%');
      event.currentTarget.style.setProperty('--zoom-y', y + '%');
    }

    _onSlideIntersect(entries) {
      entries.forEach(
        function (entry) {
          if (!entry.isIntersecting) return;
          this._setActiveMedia(entry.target.getAttribute('data-media-id'));
        }.bind(this)
      );
    }

    _onThumbnailClick(thumbnail) {
      this._scrollToMedia(thumbnail.getAttribute('data-media-id'));
    }

    _step(direction) {
      var activeIndex = this.slides.findIndex(function (slide) {
        return slide.classList.contains('is-active');
      });
      var nextIndex = (activeIndex + direction + this.slides.length) % this.slides.length;
      var slide = this.slides[nextIndex];
      if (slide) this._scrollToMedia(slide.getAttribute('data-media-id'));
    }

    _scrollToMedia(mediaId) {
      var slide = this.slides.find(function (item) {
        return item.getAttribute('data-media-id') === mediaId;
      });
      if (!slide || !this.viewer) return;
      this.viewer.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      this._setActiveMedia(mediaId);
    }

    _setActiveMedia(mediaId) {
      if (!mediaId) return;
      this.slides.forEach(function (slide) {
        slide.classList.toggle('is-active', slide.getAttribute('data-media-id') === mediaId);
      });
      this.thumbnails.forEach(function (thumbnail) {
        var isActive = thumbnail.getAttribute('data-media-id') === mediaId;
        thumbnail.classList.toggle('is-active', isActive);
        thumbnail.setAttribute('aria-selected', String(isActive));
      });
    }

    _openFullscreen(trigger) {
      if (!this.fullscreenDialog) return;
      var slide = trigger.closest('[data-gallery-slide]');
      var mediaId = slide && slide.getAttribute('data-media-id');
      this.fullscreenDialog.showModal();
      if (mediaId && this.fullscreenTrack) {
        var target = this.fullscreenTrack.querySelector('[data-media-id="' + CSS.escape(mediaId) + '"]');
        if (target) target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    }

    _closeFullscreen() {
      if (this.fullscreenDialog && this.fullscreenDialog.open) this.fullscreenDialog.close();
    }

    _onFullscreenBackdropClick(event) {
      if (event.target === this.fullscreenDialog) this._closeFullscreen();
    }

    _onVariantChange(event) {
      var mediaId = event.detail && event.detail.featuredMediaId;
      if (mediaId) this._scrollToMedia(String(mediaId));
    }
  }

  if (!customElements.get('product-gallery')) {
    customElements.define('product-gallery', ProductGalleryElement);
  }

  /* ------------------------------------------------------------------
   * Variant picker
   * ---------------------------------------------------------------- */
  function formatMoney(cents) {
    // Values in the variants JSON are already pre-formatted server-side
    // (via the `money` filter), so this is only a passthrough placeholder
    // kept for clarity at call sites below.
    return cents;
  }

  function initVariantPicker(root) {
    var picker = root.querySelector('[data-variant-picker]');
    var jsonScript = root.querySelector('[data-product-variants-json]');
    var form = document.getElementById(root.getAttribute('data-product-form-id'));
    if (!jsonScript || !form) return;

    var variants;
    try {
      variants = JSON.parse(jsonScript.textContent);
    } catch (error) {
      return;
    }

    var variantIdInput = form.querySelector('[data-variant-id]');
    var priceRoot = root.querySelector('[data-product-price]');
    var skuEl = root.querySelector('[data-product-sku]');
    var addToCartButtons = Array.from(root.querySelectorAll('[data-add-to-cart]'));

    function getSelectedOptions() {
      var values = [];
      if (!picker) return values;
      picker.querySelectorAll('[data-option-position]').forEach(function (input) {
        var position = parseInt(input.getAttribute('data-option-position'), 10);
        var isSelectable =
          (input.tagName === 'SELECT') ||
          (input.tagName === 'INPUT' && input.type === 'radio' && input.checked);
        if (isSelectable) values[position - 1] = input.value;
      });
      return values;
    }

    function findVariant(selectedOptions) {
      return variants.find(function (variant) {
        return variant.options.every(function (value, index) {
          return value === selectedOptions[index];
        });
      });
    }

    function updatePrice(variant) {
      if (!priceRoot) return;
      var sale = priceRoot.querySelector('[data-price-sale]');
      var compare = priceRoot.querySelector('[data-price-compare]');
      var regular = priceRoot.querySelector('[data-price-regular]');
      var onSale = Boolean(variant.compareAtPrice);

      if (sale) {
        sale.hidden = !onSale;
        sale.textContent = formatMoney(variant.price);
      }
      if (compare) {
        compare.hidden = !onSale;
        if (onSale) compare.textContent = formatMoney(variant.compareAtPrice);
      }
      if (regular) {
        regular.hidden = onSale;
        if (!onSale) regular.textContent = formatMoney(variant.price);
      }
    }

    function updateAvailability(variant) {
      addToCartButtons.forEach(function (button) {
        button.disabled = !variant.available;
      });
    }

    function updateOptionAvailability(selectedOptions) {
      if (!picker) return;
      picker.querySelectorAll('[data-option-position]').forEach(function (input) {
        var position = parseInt(input.getAttribute('data-option-position'), 10);
        var label = picker.querySelector('label[for="' + input.id + '"]');
        if (input.tagName !== 'INPUT' || !label) return;

        var candidate = selectedOptions.slice();
        candidate[position - 1] = input.value;

        var matches = variants.filter(function (variant) {
          return variant.options.every(function (value, index) {
            return candidate[index] === undefined || value === candidate[index];
          });
        });

        var exists = matches.length > 0;

        label.classList.toggle('is-unavailable', !exists);
      });
    }

    function syncUrl(variant) {
      if (!variant.url || !window.history || !window.history.replaceState) return;
      window.history.replaceState({}, '', variant.url);
    }

    function handleSelectionChange() {
      var selectedOptions = getSelectedOptions();
      var variant = findVariant(selectedOptions);

      updateOptionAvailability(selectedOptions);

      if (!variant) {
        addToCartButtons.forEach(function (button) {
          button.disabled = true;
        });
        return;
      }

      if (variantIdInput) variantIdInput.value = variant.id;
      // SKU is never surfaced once the shopper starts picking options.
      if (skuEl) skuEl.hidden = true;

      updatePrice(variant);
      updateAvailability(variant);
      syncUrl(variant);

      root.dispatchEvent(
        new CustomEvent('am:variant:change', {
          bubbles: true,
          detail: variant,
        })
      );
    }

    if (picker) {
      picker.addEventListener('change', handleSelectionChange);
    }

    // Run once on load so unavailable/sold-out states reflect the
    // initial selection immediately (no flash of incorrect state).
    updateOptionAvailability(getSelectedOptions());
  }

  /* ------------------------------------------------------------------
   * Quantity selector (shared by main + sticky instances)
   * ---------------------------------------------------------------- */
  function initQuantitySelectors(root) {
    var selectors = Array.from(root.querySelectorAll('[data-quantity-selector]'));

    function clamp(input) {
      var min = parseInt(input.min, 10) || 1;
      var value = parseInt(input.value, 10);
      if (isNaN(value) || value < min) value = min;
      input.value = value;
      return value;
    }

    selectors.forEach(function (selector) {
      var input = selector.querySelector('[data-quantity-input]');
      var minus = selector.querySelector('[data-quantity-minus]');
      var plus = selector.querySelector('[data-quantity-plus]');
      if (!input) return;

      function setValue(value) {
        input.value = value;
        clamp(input);
        syncOtherSelectors(value);
      }

      function syncOtherSelectors(value) {
        selectors.forEach(function (other) {
          if (other === selector) return;
          var otherInput = other.querySelector('[data-quantity-input]');
          if (otherInput) otherInput.value = value;
        });
      }

      if (minus) {
        minus.addEventListener('click', function () {
          setValue(clamp(input) - 1);
        });
      }
      if (plus) {
        plus.addEventListener('click', function () {
          setValue(clamp(input) + 1);
        });
      }
      input.addEventListener('change', function () {
        setValue(clamp(input));
      });
    });
  }

  /* ------------------------------------------------------------------
   * Sticky add-to-cart visibility
   * ---------------------------------------------------------------- */
  function initStickyCart(root) {
    var stickyCart = root.querySelector('[data-sticky-cart]');
    var sentinel = root.querySelector('[data-sticky-cart-sentinel]');
    if (!stickyCart || !sentinel || !('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        stickyCart.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      });
    });
    observer.observe(sentinel);
  }

  /* ------------------------------------------------------------------
   * Share button
   * ---------------------------------------------------------------- */
  function initShareButton(root) {
    var button = root.querySelector('[data-share-button]');
    if (!button) return;

    button.addEventListener('click', function () {
      var url = button.getAttribute('data-share-url') || window.location.href;
      var title = button.getAttribute('data-share-title') || document.title;

      if (navigator.share) {
        navigator.share({ title: title, url: url }).catch(function () {});
        return;
      }

      if (navigator.clipboard) {
        navigator.clipboard.writeText(url).then(function () {
          button.setAttribute('data-copied', 'true');
          setTimeout(function () {
            button.removeAttribute('data-copied');
          }, 2000);
        });
      }
    });
  }

  /* ------------------------------------------------------------------
   * Bootstrap: run for every `[data-product-root]` on the page (there is
   * only ever one on the product template, but this stays defensive).
   * ---------------------------------------------------------------- */
  document.querySelectorAll('[data-product-root]').forEach(function (root) {
    initVariantPicker(root);
    initQuantitySelectors(root);
    initStickyCart(root);
    initShareButton(root);
  });
})();
